import { useEffect, useState } from "react";
import { api, apiError, fileUrl, money } from "@/lib/api";
import { Text, Area, Select, Toggle, Modal, ImagesEditor } from "@/components/admin/AdminUI";
import { toast } from "sonner";
import { Plus, Pencil, Archive, ArchiveRestore } from "lucide-react";

const empty = { name: "", description: "", price: 0, category_id: "", sizes: [], measurements: "", images: [], inventory: 0, track_inventory: true, mark_sold_out: false, shipping_weight: "", shipping_info: "", status: "draft", visible: true };

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/admin/products").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/categories").then((r) => setCats(r.data)); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p, category_id: p.category_id || "" }); setEditId(p.id); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, sizes: typeof form.sizes === "string" ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean) : form.sizes };
      payload.shipping_weight = form.shipping_weight === "" || form.shipping_weight === null ? null : Number(form.shipping_weight);
      if (editId) await api.put(`/admin/products/${editId}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const archive = async (p) => { await api.patch(`/admin/products/${p.id}/archive?value=${!p.archived}`); load(); };

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-3xl text-kys-ink">Products</h1><p className="text-sm text-kys-muted">Ready-to-ship inventory.</p></div>
        <button data-testid="new-product-btn" onClick={openNew} className="flex items-center gap-1.5 rounded-full bg-kys-ink px-4 py-2 text-sm font-semibold text-white hover:bg-kys-accent"><Plus className="h-4 w-4" /> New</button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-lg border border-kys-blush-border bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-kys-blush-border bg-kys-blush text-left text-xs uppercase tracking-wide text-kys-muted">
            <tr><th className="p-3">Product</th><th className="p-3">Category</th><th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-kys-blush-border">
            {items.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.slug}`} className={p.archived ? "opacity-50" : ""}>
                <td className="p-3"><div className="flex items-center gap-3"><div className="h-12 w-10 overflow-hidden rounded bg-kys-blush">{p.images?.[0] && <img src={fileUrl(p.images[0])} alt="" className="h-full w-full object-cover" />}</div><span className="font-medium text-kys-ink">{p.name}</span></div></td>
                <td className="p-3 text-kys-charcoal">{catName(p.category_id)}</td>
                <td className="p-3 font-mono">{money(p.price)}</td>
                <td className="p-3">{p.sold_out ? <span className="text-red-600">Sold out</span> : p.inventory}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs ${p.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{p.status}</span></td>
                <td className="p-3"><div className="flex gap-2">
                  <button onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`} className="text-kys-muted hover:text-kys-accent"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => archive(p)} className="text-kys-muted hover:text-kys-accent">{p.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="p-8 text-center text-sm text-kys-muted">No products yet.</p>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Product" : "New Product"} wide>
        <div className="space-y-4">
          <Text label="Name" value={form.name} onChange={(v) => set("name", v)} testid="pf-name" />
          <Area label="Description" value={form.description} onChange={(v) => set("description", v)} />
          <div className="grid grid-cols-2 gap-4">
            <Text label="Price (USD)" type="number" value={form.price} onChange={(v) => set("price", v)} testid="pf-price" />
            <Select label="Category" value={form.category_id} onChange={(v) => set("category_id", v)} options={[{ value: "", label: "—" }, ...cats.map((c) => ({ value: c.id, label: c.name }))]} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Text label="Inventory" type="number" value={form.inventory} onChange={(v) => set("inventory", v)} testid="pf-inventory" />
            <Text label="Sizes (comma separated)" value={Array.isArray(form.sizes) ? form.sizes.join(", ") : form.sizes} onChange={(v) => set("sizes", v)} />
          </div>
          <Text label="Shipping weight (oz)" type="number" value={form.shipping_weight} onChange={(v) => set("shipping_weight", v)} testid="pf-shipping-weight" hint="Required for live shipping rates. Enter the item's weight in ounces (packaging weight is added automatically)." />
          <Area label="Measurements" value={form.measurements} onChange={(v) => set("measurements", v)} rows={2} />
          <Area label="Shipping info" value={form.shipping_info} onChange={(v) => set("shipping_info", v)} rows={2} />
          <ImagesEditor value={form.images} onChange={(v) => set("images", v)} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Toggle label="Track inventory" checked={form.track_inventory} onChange={(v) => set("track_inventory", v)} />
            <Toggle label="Mark sold out" checked={form.mark_sold_out} onChange={(v) => set("mark_sold_out", v)} />
            <Toggle label="Visible" checked={form.visible} onChange={(v) => set("visible", v)} />
          </div>
          <Select label="Status" value={form.status} onChange={(v) => set("status", v)} options={[{ value: "draft", label: "Draft (hidden)" }, { value: "published", label: "Published (live)" }]} />
          <button data-testid="save-product" onClick={save} className="w-full rounded-full bg-kys-ink py-3 text-sm font-semibold text-white hover:bg-kys-accent">Save Product</button>
        </div>
      </Modal>
    </div>
  );
}
